import type { NotificationAction, NotificationActionType } from '../../types';

/**
 * The action types the engine sends (value set `actionType`), plus the older
 * lowercase spellings mapped onto them so rows written before the engine still work.
 */
const LEGACY_TYPES: Record<string, NotificationActionType> = {
  link: 'LINK',
  deeplink: 'DEEP_LINK',
  navigate: 'DEEP_LINK',
  button: 'BUTTON',
  custom: 'BUTTON',
  dismiss: 'ACKNOWLEDGE',
  api: 'API_CALL',
};

export const actionTypeOf = (action: NotificationAction): NotificationActionType | string => {
  const raw = String(action.actionType || '').trim();
  return LEGACY_TYPES[raw] ?? raw.toUpperCase();
};

/** Whether pressing it needs the dialog first: a note to write or a question to confirm. */
export const needsDialog = (action: NotificationAction): boolean =>
  Boolean(action.requiresMemo || action.confirmMessage);

const isExternal = (url: string) => /^https?:\/\//i.test(url);

/**
 * What an action DOES in the browser, once the press has been recorded.
 *
 * Recording is the audit trail and happens for every type first; this is only the
 * part the browser owns — going somewhere. BUTTON and ACKNOWLEDGE are complete once
 * recorded, and anything that changes state runs server-side as the action's effect.
 *
 * API_CALL is deliberately not called from here: its url is database-editable, and a
 * browser firing whatever a row names, with the reader's session, is not a safe thing
 * for a notification button to do. It belongs in a server effect.
 *
 * Throws for a type this client does not know, so the reader is told rather than the
 * button quietly doing nothing.
 */
export const performAction = (
  action: NotificationAction,
  navigate: (to: string) => void,
): void => {
  const url = (action.url || '').trim();
  const type = actionTypeOf(action);

  switch (type) {
    case 'LINK':
    case 'DEEP_LINK':
    case 'FORM':
      if (!url) return;
      if (isExternal(url)) {
        // New tab, and noopener so the opened page cannot reach back into this one.
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(url.startsWith('/') ? url : `/${url}`);
      }
      return;

    case 'BUTTON':
    case 'ACKNOWLEDGE':
    case 'API_CALL':
      return;

    default:
      throw new Error(`'${action.label}' has an action type this app does not support (${type}).`);
  }
};
